import { lazy, ReactNode } from 'react'

import { AuthenticatedProviders } from '@/providers'
import LoadableSuspense from '@components/Loadable/Loadable'
import PermissionsGuard, { Permissions } from '@components/PermissionsGuard/PermissionsGuard'
import { useBoolVariation } from '@launchdarkly/react-sdk'
import { isLowerEnvironment } from '@utils/EnvironmentHelper'
import Home from '@views/Home/Home'
import Exports from '@views/MarketBaskets/Exports/Exports'
import MarketBasketProvider from '@views/MarketBaskets/MarketBasketProvider'
import Programs from '@views/MarketBaskets/Programs/Programs'
import DirectDealsReport from '@views/MarketBaskets/Reports/DirectDeals/DirectDealsReport'
import MarketBasketReports from '@views/MarketBaskets/Reports/Reports'
import { createBrowserRouter, useRouteError } from 'react-router-dom'

import Login from '../auth/Login/Login'
import AuthRedirect from './AuthRedirect'
import Root from './Root'

const Reports = LoadableSuspense(lazy(() => import('@views/Reports/Reports')))
const PlatformSettings = LoadableSuspense(
	lazy(() => import('@views/PlatformSettings/PlatformSettings'))
)
const GlobalBasketSettings = LoadableSuspense(
	lazy(() => import('@views/GlobalBasketSettings/GlobalBasketSettings'))
)
const PackConversionEditor = LoadableSuspense(
	lazy(() => import('@views/GlobalBasketSettings/PackConversions/PackConversionEditor'))
)
const TaskServer = LoadableSuspense(lazy(() => import('@views/TaskServer/TaskServer')))
const NotFound = LoadableSuspense(lazy(() => import('@views/error-pages/NotFound')))
const AwaitingImplementation = LoadableSuspense(
	lazy(() => import('@views/error-pages/AwaitingImplementation'))
)
const NotAuthorized = LoadableSuspense(lazy(() => import('@views/error-pages/NotAuthorized')))
const UserNotAdded = LoadableSuspense(lazy(() => import('@views/error-pages/UserNotAdded')))
const ServiceUnavailable = LoadableSuspense(
	lazy(() => import('@views/error-pages/ServiceUnavailable'))
)
const MuiTheme = LoadableSuspense(lazy(() => import('@views/MuiTheme/MuiTheme')))
const Report = LoadableSuspense(lazy(() => import('@views/Reports/Report/Report')))
const Users = LoadableSuspense(lazy(() => import('@views/Users/Users')))
const MarketBasketsList = LoadableSuspense(
	lazy(() => import('@views/MarketBaskets/BasketList/BasketList'))
)
const NewMarketBasket = LoadableSuspense(
	lazy(() => import('@views/MarketBaskets/Form/NewMarketBasket'))
)
const EditMarketBasket = LoadableSuspense(
	lazy(() => import('@views/MarketBaskets/Form/EditMarketBasket'))
)
const MarketBasket = LoadableSuspense(lazy(() => import('@views/MarketBaskets/View/MarketBasket')))
const SwitchTracking = LoadableSuspense(lazy(() => import('@views/SwitchTracking/SwitchTracking')))
const router = createBrowserRouter([
	{
		path: '/',
		ErrorBoundary: () => {
			// This bubbles up the error to the root level
			throw useRouteError()
		},
		element: <Root />,
		children: [
			{
				path: '',
				element: <AuthRedirect />,
			},
			{
				path: '/login',
				element: <Login />,
			},
			{
				path: '/app',
				element: <AuthenticatedProviders />,
				children: [
					{
						path: '',
						element: <Home />,
					},

					{
						path: 'reports',
						element: <Reports />,
					},
					{
						path: 'reports/:reportId',
						element: <Report />,
					},
					{
						path: 'reports/:reportId/:personalizedId',
						element: <Report />,
					},
					{
						path: 'settings',
						element: (
							<PermissionsGuard showWhenHasPermission={[Permissions.WonderlandView]}>
								<PlatformSettings />
							</PermissionsGuard>
						),
					},
					{
						path: 'market-baskets',
						element: (
							<PermissionsGuard showWhenHasPermission={[Permissions.BasketEdit]}>
								<MarketBasketsList />
							</PermissionsGuard>
						),
					},
					{
						path: 'market-baskets/new',
						element: (
							<PermissionsGuard showWhenHasPermission={[Permissions.BasketEdit]}>
								<NewMarketBasket />
							</PermissionsGuard>
						),
					},
					{
						path: 'market-baskets/analytics',
						element: (
							<PermissionsGuard showWhenHasPermission={[Permissions.BiDashboardView]}>
								<MarketBasketReports />
							</PermissionsGuard>
						),
					},
					{
						path: 'market-baskets/analytics/direct-deals',
						element: (
							<PermissionsGuard showWhenHasPermission={[Permissions.BiDashboardView]}>
								<DirectDealsReport />
							</PermissionsGuard>
						),
					},
					{
						path: 'market-baskets/:marketBasketId/edit',
						element: (
							<PermissionsGuard showWhenHasPermission={[Permissions.BasketEdit]}>
								<MarketBasketProvider>
									<EditMarketBasket />
								</MarketBasketProvider>
							</PermissionsGuard>
						),
					},
					{
						path: 'market-baskets/:marketBasketId',
						element: (
							<PermissionsGuard showWhenHasPermission={[Permissions.BasketEdit]}>
								<MarketBasketProvider>
									<MarketBasket />
								</MarketBasketProvider>
							</PermissionsGuard>
						),
					},
					{
						path: 'market-baskets/:marketBasketId/exports/:exportId',
						element: (
							<PermissionsGuard showWhenHasPermission={[Permissions.BasketEdit]}>
								<MarketBasketProvider>
									<Exports />
								</MarketBasketProvider>
							</PermissionsGuard>
						),
					},
					{
						// FIXME: Need to rethink how this works. It got a bit messy with the similar products.
						// We should have the BE allow us to send the row id of the sim product for the programs
						path: 'market-baskets/:marketBasketId/similar-products/:similarProductId/programs',
						element: (
							<PermissionsGuard showWhenHasPermission={[Permissions.BasketEdit]}>
								<MarketBasketProvider>
									<Programs />
								</MarketBasketProvider>
							</PermissionsGuard>
						),
					},
					{
						path: 'switch-tracking',
						element: (
							<PermissionsGuard showWhenHasPermission={[Permissions.SwitchTracking]}>
								<FeatureFlagRoute flag="feature-art-1567-switch-tracking">
									<SwitchTracking />
								</FeatureFlagRoute>
							</PermissionsGuard>
						),
					},
					{
						path: 'global-basket-settings',
						element: (
							<PermissionsGuard showWhenHasPermission={[Permissions.MarketBasketSettings]}>
								<GlobalBasketSettings />
							</PermissionsGuard>
						),
					},
					{
						path: 'global-basket-settings/pack-conversion-editor',
						element: (
							<PermissionsGuard showWhenHasPermission={[Permissions.MarketBasketSettings]}>
								<FeatureFlagRoute flag="feature-art-1447-pack-conversion-editor">
									<PackConversionEditor />
								</FeatureFlagRoute>
							</PermissionsGuard>
						),
					},
					{
						path: 'task-server',
						element: (
							<PermissionsGuard showWhenHasPermission={[Permissions.TaskServerView]}>
								<TaskServer />
							</PermissionsGuard>
						),
					},
					{
						path: 'user-management',
						element: (
							<PermissionsGuard showWhenHasPermission={[Permissions.ManageUsers]}>
								<Users />
							</PermissionsGuard>
						),
					},
				],
			},
			...(isLowerEnvironment
				? [
						{
							path: 'muitheme',
							element: <MuiTheme />,
						},
					]
				: []),
			{
				path: '*',
				children: [
					{
						path: 'notauthorized',
						element: <NotAuthorized />,
					},
					{
						path: '503',
						element: <ServiceUnavailable />,
					},
					{
						path: '403',
						element: <UserNotAdded />,
					},
					{
						path: '*',
						element: <NotFound />,
					},
				],
			},
		],
	},
])

export default router

// Leave when FF is removed
function FeatureFlagRoute({ flag, children }: { flag: string; children: ReactNode }) {
	const enabled = useBoolVariation(flag, false)
	if (!enabled) return <NotFound />
	return children
}
